import { ITaskScheduleReminder } from "../shared/db";

export const TASK_SCHEDULE_REMINDER_MAX_COUNT = 5;
export const TASK_SCHEDULE_REMINDER_TITLE_MAX_LENGTH = 40;
export const TASK_SCHEDULE_REMINDER_MESSAGE_MAX_LENGTH = 100;

export type TaskScheduleReminderDTO = Pick<
  ITaskScheduleReminder,
  "secondsBeforeExpiration" | "title" | "message"
>;
export type TaskScheduleReminderFormItem = TaskScheduleReminderDTO & Partial<ITaskScheduleReminder>;

const minuteSeconds = 60;
const hourSeconds = 60 * minuteSeconds;
const daySeconds = 24 * hourSeconds;

export const formatTaskScheduleReminderDuration = (seconds: number): string => {
  const roundedSeconds = Math.max(0, Math.round(seconds));
  const days = Math.floor(roundedSeconds / daySeconds);
  const hours = Math.floor((roundedSeconds - days * daySeconds) / hourSeconds);
  const minutes = Math.floor(
    (roundedSeconds - days * daySeconds - hours * hourSeconds) / minuteSeconds,
  );
  const parts: string[] = [];

  if (days) parts.push(`${days} ${days > 1 ? "days" : "day"}`);
  if (hours) parts.push(`${hours} ${hours > 1 ? "hours" : "hour"}`);
  if (minutes) parts.push(`${minutes} ${minutes > 1 ? "minutes" : "minute"}`);

  return parts.join(" ") || "0 minutes";
};

export const formatTaskScheduleReminderOffset = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds < minuteSeconds) {
    return "soon";
  }
  if (seconds === hourSeconds) {
    return "an hour";
  }
  return formatTaskScheduleReminderDuration(seconds);
};

export const createDefaultTaskScheduleReminderTitle = (secondsBeforeExpiration: number): string =>
  `Task expiring in ${formatTaskScheduleReminderOffset(secondsBeforeExpiration)}`;

export const createTaskScheduleReminder = ({
  secondsBeforeExpiration,
  taskName,
}: {
  secondsBeforeExpiration: number;
  taskName: string;
}): TaskScheduleReminderFormItem => ({
  secondsBeforeExpiration,
  title: createDefaultTaskScheduleReminderTitle(secondsBeforeExpiration),
  message: taskName,
});

export const createDefaultTaskScheduleReminderList = (
  taskName: string,
): TaskScheduleReminderFormItem[] =>
  [15 * minuteSeconds, hourSeconds].map((secondsBeforeExpiration) =>
    createTaskScheduleReminder({ secondsBeforeExpiration, taskName }),
  );

export const normalizeTaskScheduleReminderList = (
  reminderList: Partial<ITaskScheduleReminder>[] | undefined,
): TaskScheduleReminderDTO[] =>
  (reminderList ?? [])
    .map((reminder) => ({
      secondsBeforeExpiration: Math.round(reminder.secondsBeforeExpiration ?? 0),
      title: `${reminder.title ?? ""}`.slice(0, TASK_SCHEDULE_REMINDER_TITLE_MAX_LENGTH),
      message: `${reminder.message ?? ""}`.slice(0, TASK_SCHEDULE_REMINDER_MESSAGE_MAX_LENGTH),
    }))
    .filter(
      (reminder) =>
        reminder.secondsBeforeExpiration > 0 && reminder.title.trim() && reminder.message.trim(),
    );
